import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Copy, Download, Mail, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const statusColors = {
  enquiry: 'bg-slate-100 text-slate-700 border-slate-300',
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  processing: 'bg-blue-100 text-blue-800 border-blue-300',
  pending_payment: 'bg-orange-100 text-orange-800 border-orange-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
  refunded: 'bg-purple-100 text-purple-800 border-purple-300',
};

const OrderHeader = ({
  orderData,
  currentStatus,
  hasUnsavedChanges,
  saving,
  onSave,
  onGeneratePDF,
  onSendEmail,
  onStatusChange,
}) => {
  const navigate = useNavigate();
  const status = currentStatus ?? orderData.status;

  const copyOrderNumber = async () => {
    const orderNumber = orderData?.order_number;
    if (!orderNumber) return;
    try {
      await navigator.clipboard.writeText(String(orderNumber));
      toast.success('Order number copied');
    } catch (error) {
      toast.error('Could not copy order number');
    }
  };

  const statusOptions = [
    { value: 'enquiry', label: 'Enquiry' },
    { value: 'pending_payment', label: 'Pending Payment' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm mb-2" data-testid="edit-order-header">
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/orders')}
            className="h-8 w-8 shrink-0 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="truncate text-lg font-semibold text-slate-900">
            Order {orderData.order_number}
          </h1>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-slate-500 hover:text-slate-900"
            onClick={copyOrderNumber}
            title="Copy order number"
            data-testid="edit-order-copy-order-number-button"
          >
            <Copy className="w-3.5 h-3.5" />
          </Button>
          <Select
            value={status}
            onValueChange={(value) => onStatusChange && onStatusChange(value)}
          >
            <SelectTrigger className={`h-8 w-[150px] border font-medium ${statusColors[status] || statusColors.pending_payment}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="capitalize">{option.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasUnsavedChanges && (
            <Button
              onClick={onSave}
              disabled={saving}
              size="sm"
              className="h-8 bg-green-600 hover:bg-green-700 text-white"
              data-testid="status-save-button"
            >
              <Save className="w-3 h-3 mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onGeneratePDF}
            className="h-8 text-slate-700 border-slate-300"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Download Invoice
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onSendEmail}
            className="h-8 text-slate-700 border-slate-300"
          >
            <Mail className="w-4 h-4 mr-1.5" />
            Send Email
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderHeader;
